// The only place this module talks to the transparency funnel stats API
// (transparency `src/lib/landing/stats.ts`, served at `/v1/landing/*`). Every
// response is typed as the S8 envelope, so `meta` is never optional.
//
// The portal is not a backend (I-4): nothing here recomputes a figure. Every
// count and every conversion rate is the Worker's; this module only formats.

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export const WINDOWS = ["7", "30", "90"] as const;
export type WindowDays = 7 | 30 | 90;

export type FunnelSummary = {
  funnel_slug: string;
  page_path: string;
  views: number;
  new_visitors: number;
  /** Taps on an app-entry page's button. Optional so an API deployed before taps still renders. */
  clicks?: number;
  /** Sign-ups since visitor counting began: the numerator of `conversion_pct`. */
  signups: number;
  /**
   * Every sign-up in the window, on the same basis as `reached_drip`. Optional
   * so an API deployed before it existed still renders (falls back to `signups`).
   */
  signups_in_window?: number;
  /** `null` when there were no new visitors — never a 0% rate. */
  conversion_pct: number | null;
  reached_drip: number;
  handover_pending: number;
  handover_failed: number;
  /** First day visitor counting has data for this funnel, or `null` if none yet. */
  counting_since: string | null;
};

export type FunnelsData = { days: number; funnels: FunnelSummary[] };

export type VariantStats = {
  variant: string;
  views: number;
  new_visitors: number;
  /** Taps on an app-entry page's button; optional for an older API. */
  clicks?: number;
  signups: number;
  conversion_pct: number | null;
};

export type DailyRow = {
  day: string;
  variant: string;
  views: number;
  new_visitors: number;
  signups: number;
};

export type FunnelDetail = {
  funnel_slug: string;
  page_path: string;
  days: number;
  splitting: boolean;
  counting_since: string | null;
  variants: VariantStats[];
  /** How the cards compare versions (site `src/lib/landing/like-for-like.ts`); absent from an older Worker. */
  comparison?: VariantComparison;
  daily: DailyRow[];
  handover: { sent: number; pending: number; failed: number; stuck: number };
  signups_before_counting: number;
};

/**
 * The cards count a version only in the languages where every version has had a visitor, and only
 * from the day the last of them went live there; everything else is named here.
 */
export type VariantComparison = {
  basis: "like_for_like" | "single_version";
  versions: string[];
  left_out: { new_visitors: number; signups: number };
  languages_without: { locale: string; missing: string[]; new_visitors: number; signups: number }[];
  late_languages: { locale: string; from: string; new_visitors: number; signups: number }[];
};

// Same-origin via the Pages Function proxy (functions/landing-pages/[[path]].ts).
const BASE = import.meta.env.VITE_LANDING_PAGES_API_BASE ?? "/landing-pages/v1";

export class LandingPagesError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Landing Pages API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * How long the console waits for any landing request. A request that outlives it ends as a named
 * `timeout` error the panel can show, never a button stuck on "Working…" (bug 3fd0402f, rec. 2).
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/** A write may run several GitHub calls in turn on the Worker (each capped at 10 s there). */
export const WRITE_TIMEOUT_MS = 60_000;
/** Approve all / Reject all decides every language in turn. */
export const BATCH_TIMEOUT_MS = 180_000;

async function call(path: string, init: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS): Promise<Response> {
  try {
    return await fetch(`${BASE}${path}`, { ...init, signal: AbortSignal.timeout(timeoutMs) });
  } catch (error) {
    if (error instanceof Error && (error.name === "TimeoutError" || error.name === "AbortError")) {
      throw new LandingPagesError(0, { error: "timeout" });
    }
    throw error;
  }
}

async function get<T>(path: string): Promise<Enveloped<T>> {
  const response = await call(path, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new LandingPagesError(response.status, await safeBody(response));
  }
  return (await response.json()) as Enveloped<T>;
}

export const getFunnels = (days: WindowDays) => get<FunnelsData>(`/funnels?days=${days}`);

/** Optional cuts the Worker applies to every figure, card and chart point. `null` = all. */
export type FunnelCut = { source: string | null; locale: string | null };

/** `&source=..&locale=..` for a cut; shared with the funnel stats API so both read the same cut. */
export const cutQuery = (cut?: FunnelCut) =>
  (cut?.source ? `&source=${encodeURIComponent(cut.source)}` : "") +
  (cut?.locale ? `&locale=${encodeURIComponent(cut.locale)}` : "");

export const getFunnel = (slug: string, days: WindowDays, cut?: FunnelCut) =>
  get<FunnelDetail>(`/funnels/${encodeURIComponent(slug)}?days=${days}${cutQuery(cut)}`);

export type PageStatus = "draft" | "approved" | "retired";

/** One landing page folder (transparency `src/lib/landing/pages.ts`). */
export type PageSummary = {
  folder: string;
  funnel_slug: string;
  locale: string;
  variant: string;
  status: PageStatus;
  /** Served to visitors now (approved with an approval record). */
  live: boolean;
  launch_at: string;
  page_path: string;
  approval: { by: string; at: string } | null;
  /** `null` when the Worker could not read sign-up counts. */
  signups: number | null;
  /** New visitors since counting began; optional so an older API still renders. */
  visitors?: number | null;
  views?: number | null;
  /** Taps on an app-entry page's button (`/api/landing-click`); optional so an older API still renders. */
  clicks?: number | null;
  /** Sign-ups the conversion rate is computed over. */
  signups_counted?: number | null;
  /** The Worker's rate; `null` with no visitors, never 0%. */
  conversion_pct?: number | null;
  /** R6: a console change for this folder is waiting for approval. */
  pending?: boolean;
};

/** One funnel's figures, computed by the Worker from its rows (never summed here, I-4). */
export type FunnelTotals = {
  visitors: number | null;
  views: number | null;
  /** Taps on an app-entry page's button; optional for an older API. */
  clicks?: number | null;
  signups: number | null;
  signups_counted: number | null;
  conversion_pct: number | null;
};

export type PagesData = {
  days: number;
  counts_available: boolean;
  /** R6: `false` when pending console changes could not be read. */
  pending_available?: boolean;
  funnels: { funnel_slug: string; totals?: FunnelTotals; pages: PageSummary[] }[];
};

export const getPages = (days: WindowDays) => get<PagesData>(`/pages?days=${days}`);

export type PageAsset = {
  slot: "hero.back_screen" | "hero.front_screen" | "how_screen";
  label: string;
  /** The page folder's file name, build hash removed. */
  file: string;
  /** Public, absolute image URL. */
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
};

/** Counts only: the Worker never sends a person (R5 spec, PII decision). */
export type PageLeads =
  | {
      counts_available: true;
      total: number;
      in_drip: number;
      pending: number;
      failed: number;
      stuck: number;
      daily: { day: string; leads: number }[];
    }
  | {
      counts_available: false;
      total: null;
      in_drip: null;
      pending: null;
      failed: null;
      stuck: null;
      daily: null;
    };

/** One landing page folder in full (transparency `src/lib/landing/page-detail.ts`). */
export type PageDetailData = {
  days: number;
  page: Omit<PageSummary, "signups" | "approval"> & {
    template: string;
    template_source: string | null;
    approval: { by: string; at: string; evidence: string } | null;
  };
  copy: {
    seo: { title: string; description: string };
    hero: {
      kicker: string;
      headline: string;
      sub: string;
      card_heading: string;
      card_body: string;
      button: string;
      proof: string | null;
    };
    points_heading: string;
    points: { title: string; body: string }[];
    how_heading: string;
    steps: string[];
    bottom: { heading: string; body: string; button: string };
    success: string;
  };
  assets: PageAsset[];
  siblings: { folder: string; locale: string; variant: string; status: "draft" | "approved"; live: boolean }[];
  leads: PageLeads;
};

export const getPage = (folder: string, days: WindowDays) =>
  get<PageDetailData>(`/pages/${encodeURIComponent(folder)}?days=${days}`);

// ---- R6 write path (transparency specs/2026-09-25-landing-r6-write-path.md) ----
// Every write becomes a commit on the page folder's pending PR; only approval merges it.

async function send<T>(method: "POST" | "PUT", path: string, body: unknown, timeoutMs = WRITE_TIMEOUT_MS): Promise<Enveloped<T>> {
  const response = await call(path, {
    method,
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }, timeoutMs);
  if (!response.ok) {
    throw new LandingPagesError(response.status, await safeBody(response));
  }
  return (await response.json()) as Enveloped<T>;
}

export type Me = { email: string; can_edit: boolean; can_approve: boolean };
export const getMe = () => get<Me>("/me");

export type PullRequest = { number: number; url: string };

export const createVersion = (input: { funnel: string; locale: string; start_from: string }) =>
  send<{ folder: string; variant: string; pr: PullRequest }>("POST", "/pages", input);

/** R6b: every language live on a Shopify storefront, and which of them this funnel has no page in. */
export type Languages = {
  funnel: string;
  store_languages: string[];
  page_languages: string[];
  /** `routable: false` = the site has no page address in that language yet (engineering work). */
  new_languages: { code: string; routable: boolean }[];
};
export const getLanguages = (funnel: string) => get<Languages>(`/languages?funnel=${encodeURIComponent(funnel)}`);

/** `data` is the file's bytes, base64. JSON rather than multipart: see the spec. */
export const replaceAsset = (folder: string, slot: PageAsset["slot"], input: { filename: string; alt: string; data: string }) =>
  send<{ pr: PullRequest }>("PUT", `/pages/${encodeURIComponent(folder)}/assets/${slot}`, input);

export const retirePage = (folder: string) =>
  send<{ pr: PullRequest }>("POST", `/pages/${encodeURIComponent(folder)}/retire`, { confirm: folder });

/** Recover: put a retired version back live (the mirror of retire). */
export const recoverPage = (folder: string) =>
  send<{ pr: PullRequest }>("POST", `/pages/${encodeURIComponent(folder)}/recover`, { confirm: folder });

export type ChangeAction = "new_version" | "replace_asset" | "retire" | "recover";

export type ChangePreview = { status: "building" | "ready" | "failed"; url: string | null; run_url: string | null };

export type ChangeData = {
  pending: {
    pr: PullRequest;
    head_sha: string;
    changes: { action: ChangeAction; detail: string; by: string; at: string }[];
    /** R6c: the draft built on a private preview site. Absent on an API deployed before R6c. */
    preview?: ChangePreview | null;
    /** Set once Damien approved: the approval is on the PR and it merges when its checks pass. */
    approval?: Approval | null;
  } | null;
  effect: { kind: "new_version" | "retire" | "recover" | "assets"; locale: string; live_before: string[]; live_after: string[] } | null;
  deploy: {
    merge_sha: string;
    status: string;
    conclusion: string | null;
    url: string | null;
    /** Where the merge is: a release candidate on staging until promoted, then live. Absent on an older Worker. */
    release?: ReleaseState | null;
  } | null;
};

export type Approval = {
  by: string;
  at: string;
  checks: "pending" | "success" | "failure";
  checks_url: string | null;
  /** GitHub merges it by itself when the checks pass, even with this page closed. */
  auto_merge: boolean;
  /** Bug 3fd0402f rec 3: the console finisher completes it server-side, so this page may close. Absent on an older Worker. */
  server_finish?: boolean;
  /**
   * Why server_finish is false: `foreign_commit` (a commit on the change was not made by the console, so it can never
   * merge from here) or `retry_used` (the finisher re-ran a failed check once and it failed again). Absent on a Worker
   * that does not send it; finishBlocked() then infers it.
   */
  finish_blocked?: "foreign_commit" | "retry_used" | null;
};

/** One Promote release run of a release: GitHub's status (queued, in_progress, completed) and its conclusion. */
export type PromotionRun = { status: string; conclusion: string | null; url: string | null };

export type ReleaseState = {
  release_id: string | null;
  state: string;
  live: boolean;
  /** Bug 3fd0402f rec 3: the console finisher promotes a console merge by itself; this is that run once dispatched. Absent on an older Worker. */
  promotion?: PromotionRun | null;
};

/** `pr`: after an approve that is waiting for checks, the answer carries the merge once GitHub made it. */
export const getChange = (folder: string, mergeSha?: string, pr?: number) => {
  const query = mergeSha ? `?merge_sha=${mergeSha}` : pr ? `?pr=${pr}` : "";
  return get<ChangeData>(`/pages/${encodeURIComponent(folder)}/change${query}`);
};

export type ApproveResult = {
  /** Absent on an older Worker, which only ever answered with a merge. */
  state?: "merged" | "waiting_checks" | "checks_failed";
  merge_sha?: string | null;
  pr?: PullRequest;
  head_sha?: string;
  checks_url?: string | null;
  auto_merge?: boolean;
};

export const decideChange = (folder: string, decision: "approve" | "reject", headSha: string) =>
  send<ApproveResult>("POST", `/pages/${encodeURIComponent(folder)}/change/${decision}`, { head_sha: headSha });

// ---- Bulk mode (transparency specs/2026-09-29-landing-console-bulk.md) ----
// A batch is every open console change sharing one Console-Batch id: one variant across languages.

export type ChangeEffect = NonNullable<ChangeData["effect"]>;

export type BatchItem = {
  folder: string;
  locale: string;
  variant: string;
  pr: PullRequest;
  head_sha: string;
  changes: { action: ChangeAction | "approve"; detail: string; by: string; at: string; batch?: string }[];
  effect: ChangeEffect | null;
  preview: ChangePreview | null;
};

export type Batch = {
  batch_id: string;
  kind: "new_version" | "retire" | "recover" | "assets" | "mixed";
  variants: string[];
  by: string;
  at: string;
  items: BatchItem[];
};

export type BatchesData = { funnel: string; batches: Batch[] };

export const getBatches = (funnel: string) => get<BatchesData>(`/batches?funnel=${encodeURIComponent(funnel)}`);

export type BatchResult = { folder: string; ok: boolean; state?: ApproveResult["state"]; merge_sha?: string; error?: string };

export const decideBatch = (batchId: string, decision: "approve" | "reject", heads: Record<string, string>) =>
  send<{ results: BatchResult[] }>("POST", `/batches/${encodeURIComponent(batchId)}/${decision}`, { heads }, BATCH_TIMEOUT_MS);

export type RetireVariantResult = {
  batch_id: string | null;
  retired: { folder: string; locale: string; pr: PullRequest }[];
  refused: { folder: string; locale: string; reason: "last_live_version" }[];
};

export const retireVariant = (funnel: string, variant: string) =>
  send<RetireVariantResult>("POST", `/funnels/${encodeURIComponent(funnel)}/retire-variant`, {
    variant,
    confirm: `${funnel}-${variant}`,
  });

export type RecoverVariantResult = {
  batch_id: string | null;
  recovered: { folder: string; locale: string; pr: PullRequest }[];
  refused: { folder: string; locale: string; reason: "pending_change" | (string & {}) }[];
};

export const recoverVariant = (funnel: string, variant: string) =>
  send<RecoverVariantResult>("POST", `/funnels/${encodeURIComponent(funnel)}/recover-variant`, {
    variant,
    confirm: `${funnel}-${variant}`,
  });

/** One sentence per refused write, in the marketer's words. */
export function writeMessage(error: unknown): string {
  const code =
    error instanceof LandingPagesError && error.body !== null && typeof error.body === "object"
      ? String((error.body as { error?: unknown }).error ?? "")
      : "";
  const messages: Record<string, string> = {
    stale_change: "Someone changed this page a moment ago. Reload to see their change, then try again.",
    last_live_version: "This is the only live version of this page. Retiring it would take the page down.",
    not_live: "Only a live version can be retired.",
    not_retired: "Only a retired version can be recovered.",
    pending_change: "This page already has a change waiting for approval. Approve or reject that first.",
    confirm_mismatch: "The name you typed does not match the page name.",
    asset_type: "That file is not a PNG or JPG.",
    asset_too_large: "That file is too large. Keep images under 2 MB, PNG or JPG.",
    asset_name: "That file name cannot be used. Rename it with letters and numbers only.",
    undeclared_language: "This funnel has no page address in that language yet. Adding a language is engineering work.",
    bad_source: "That starting page could not be found.",
    not_approver: "Only Damien can approve or reject page changes.",
    no_change: "There is no pending change on this page any more.",
    bad_batch: "That group of changes could not be found. Reload and try again.",
    github_unavailable: "The change could not be saved because GitHub did not answer. Try again shortly.",
    github_timeout: "GitHub took too long to answer. Your approval may already be recorded: this panel checks again now. Try again if it still asks.",
    timeout: "The console got no answer in time. What you asked for may still have gone through: this view checks again now, so look before you try again.",
    merge_blocked: "The change is approved and its checks passed, but GitHub refused to publish it. Try again; if it fails twice, an engineer needs to look at the pull request.",
    draft_unreadable: "This draft could not be read. An engineer needs to look at the page file.",
    github_app_key: "The console could not sign in to GitHub, so nothing was saved. An engineer needs to check the console's GitHub key.",
    forbidden: "You do not have permission to change landing pages.",
  };
  if (messages[code]) return messages[code];
  // Anything unmapped still names its cause, so a screenshot is enough to find it: the Worker's
  // code and detail, else the HTTP status, else that the request never got an answer.
  const fallback = "The change could not be saved. Nothing was changed. Try again shortly.";
  const detail =
    error instanceof LandingPagesError && error.body !== null && typeof error.body === "object"
      ? String((error.body as { detail?: unknown }).detail ?? "")
      : "";
  const cause =
    code && detail ? `${code}: ${detail}` : code || (error instanceof LandingPagesError ? `HTTP ${error.status}` : "no response");
  return `${fallback} (${cause})`;
}

/** One query key per read, shared by the tabs and the freshness bridge. */
export const funnelsKey = (days: WindowDays) => ["landing_pages", "funnels", days] as const;
export const pagesKey = (days: WindowDays) => ["landing_pages", "pages", days] as const;
export const pageKey = (folder: string, days: WindowDays) => ["landing_pages", "page", folder, days] as const;
export const funnelKey = (slug: string, days: WindowDays, cut?: FunnelCut) =>
  ["landing_pages", "funnel", slug, days, cut?.source ?? "*", cut?.locale ?? "*"] as const;
export const meKey = ["landing_pages", "me"] as const;
export const languagesKey = (funnel: string) => ["landing_pages", "languages", funnel] as const;
export const changeKey = (folder: string, mergeSha?: string, pr?: number) =>
  pr && !mergeSha
    ? (["landing_pages", "change", folder, null, pr] as const)
    : (["landing_pages", "change", folder, mergeSha ?? null] as const);
export const batchesKey = (funnel: string) => ["landing_pages", "batches", funnel] as const;

/** The Worker could not find this page folder (deployed or as a readable draft). */
export function isUnknownPage(error: unknown): boolean {
  return (
    error instanceof LandingPagesError &&
    error.status === 404 &&
    error.body !== null &&
    typeof error.body === "object" &&
    (error.body as { error?: unknown }).error === "unknown_page"
  );
}

/**
 * One message per API failure. `areaName` comes from the registry via the
 * shell (I-5), never a literal, so a 403 names the right area.
 */
export function messageFor(error: unknown, areaName: string): string {
  if (!(error instanceof LandingPagesError)) {
    return "Landing page figures could not be read, so nothing is shown. Try again shortly.";
  }
  switch (error.status) {
    case 400:
      return "That time window is not one this view reports on.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to see this. Ask the ${areaName} area owner.`;
    case 404:
      return isUnknownPage(error)
        ? "That page folder is not in the deployed site. It may have been renamed, or its change is not live yet."
        : "That landing page has no visits or sign-ups recorded yet.";
    case 503:
      return "Landing page figures are not configured on the server, so nothing is shown.";
    default:
      return "Landing page figures could not be read, so nothing is shown. Try again shortly.";
  }
}
